import React, { useCallback, useEffect, useState } from 'react';

import { useUnmountEffect } from '@react-hookz/web';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { RowSelectionState } from '@tanstack/table-core';
import * as navigation from 'app-level/navigation';
import offeringCreatedImgPath from 'assets/pngs/carve-confirmation.png';
import validationImgPath from 'assets/pngs/validation.png';
import { useFeatureFlagVersion } from 'context/FeatureFlagContext';
import { keys, values } from 'lodash';
import { DateTime } from 'luxon';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'react-tooltip';
import { DialogBodyText, ModalNotStyled, StyledHeading } from 'ui-kit';
import { Dialog, useDialog, DialogActions } from 'ui-kit';
import ConfirmDialog from 'ui-kit/common/ConfirmDialog';

import { gql, useMutation, useQuery } from '@apollo/client';

import { Agency, AssetClass, ListingState } from '__generated__/globalTypes';

import { ArchivePools, ArchivePoolsVariables } from 'mutation/__generated__/ArchivePools';
import {
  CreatePublicOffering,
  CreatePublicOfferingVariables,
} from 'mutation/__generated__/CreatePublicOffering';
import { ARCHIVE_POOL } from 'mutation/archivePool';
import { CREATE_PUBLIC_OFFERING } from 'mutation/createPublicOffering';

import { GetPools, GetPools_user_company_pools as Pool } from 'query/__generated__/GetPools';
import { GET_POOLS } from 'query/getPools';
import { GET_COMPANY_JOBS } from 'query/jobs';

import CreateOfferingForm, { FormValues } from './CreateOfferingForm';
import { DocumentTemplatesFragments } from './DocumentLibrary/DocumentTemplates.fragments';
import { PortfolioLayout } from './Portfolio/PortfolioLayout';
import { transformSelectedPoolsData, usePortfolioContext } from './PortfolioContext';
import { restrictedResizeColumnIds } from '../../../app-level/config/TableRestrictions';
import { Button, DataDisplayTable, DataDisplayTableProps } from '../../../ui-kit';

interface PoolTableProps {
  data: Pool[];
  selection?: DataDisplayTableProps<Pool, keyof Pool>['selection'];
}

const GET_TEMPLATE_DOCUMENTS = gql`
  query GetTemplateDocuments {
    user {
      id
      company {
        id
        regulatory_agency
        templates {
          id
          ...TemplateDocument
        }
      }
    }
  }
  ${DocumentTemplatesFragments.document}
`;

const PoolTable = ({ data, selection }: PoolTableProps) => {
  const [sorting, setSorting] = useState<SortingState>([]);

  const { formatNumber } = useIntl();

  const formatCentsToDollars = (cents: number): string => {
    if (isNaN(cents) || cents == null) return '-';
    return formatNumber(cents / 100, { style: 'currency', currency: 'USD' });
  };

  const toPercentage = (bps: number): string => {
    if (isNaN(bps) || bps == null) return '-';
    return formatNumber(bps / 10000, { style: 'percent', minimumFractionDigits: 2 });
  };

  const formatDateTime = (date: string): string => {
    if (!date) return '-';
    const luxonDate = DateTime.fromISO(date);
    return luxonDate.toFormat("MM/dd/yy ' @ ' h:mma ZZZZ");
  };

  const getCellValue = (pool: Pool, activeValue: string | React.ReactNode, inactiveValue = '') => {
    return pool.state !== ListingState.ACTIVE ? inactiveValue : activeValue;
  };

  const handleSortingChange = (newSorting: SortingState) => {
    setSorting(newSorting);
  };

  const columns: ColumnDef<Pool, keyof Pool>[] = [
    {
      accessorKey: 'is_public',
      header: 'Pool Status',
      cell: (props) => {
        return props.getValue() ? (
          <div className="line-height[16px] inline-block w-[152px] rounded bg-green-400 px-2 py-1 text-center font-['PT_Sans_Narrow'] text-[14px] font-bold text-green-900">
            In Market
          </div>
        ) : (
          <div className="line-height[16px] inline-block w-[152px] rounded bg-gray-900 px-2 py-1 text-center font-['PT_Sans_Narrow'] text-[14px] font-bold text-white">
            Off Market
          </div>
        );
      },
    },
    {
      accessorKey: 'name',
      header: 'Loan Pool Name',
    },
    {
      accessorKey: 'created_date',
      header: 'Date Created',
      cell: (props) => <span>{formatDateTime(props.getValue())}</span>,
    },
    {
      accessorKey: 'asset_class',
      header: 'Asset Class',
      cell: (props) => (
        <span>
          {props.row.original.asset_class !== 'HOME' ? props.row.original.asset_class : 'RESI'}
        </span>
      ),
    },
    {
      accessorKey: 't_current_balance_cents',
      header: 'UPB',
      cell: (props) => {
        const pool = props.row.original;
        return (
          <span>
            {getCellValue(pool, formatCentsToDollars(Number(props.getValue())), 'Calculating...')}
          </span>
        );
      },
      meta: {
        numeric: true,
        alignment: 'left',
      },
    },
    {
      accessorKey: 'loan_count',
      header: 'Total in Pool',
      cell: (props) => {
        const pool = props.row.original;
        return <span>{getCellValue(pool, props.getValue())}</span>;
      },
      meta: {
        numeric: true,
        alignment: 'left',
      },
    },
    {
      accessorKey: 'wa_coupon_bps',
      cell: (props) => {
        const pool = props.row.original;
        return <span>{getCellValue(pool, toPercentage(Number(props.getValue())))}</span>;
      },
      header: 'GWAC',
      meta: {
        numeric: true,
        alignment: 'left',
      },
    },
  ];

  return (
    <DataDisplayTable
      // @ts-expect-error - TS doesn't like the fact that we're omitting the listing column
      data={data}
      // @ts-expect-error - TS doesn't like the fact that we're omitting the listing column
      columns={columns}
      selection={selection}
      noDataMessage="Please go to Loan Level Data to create a pool"
      // @ts-expect-error - TS doesn't like the fact that we're omitting the listing column
      sorting={{ state: sorting, onSortingChanged: handleSortingChange }}
      resizeCfg={restrictedResizeColumnIds.myPortfolio.poolManager}
    />
  );
};

const PoolManager = () => {
  const { setSelectedPoolsData } = usePortfolioContext();
  const { data: docData, loading } = useQuery(GET_TEMPLATE_DOCUMENTS);
  const documents = docData?.user.company.templates;
  const isCreditUnion = docData?.user.company.regulatory_agency === Agency.NCUA;
  const loanWriteMode = useFeatureFlagVersion('loanWriteMode');
  const isArchiveEnabled = loanWriteMode === 'hybrid' || loanWriteMode === 'v1';
  const asyncCreationVersion = useFeatureFlagVersion('asyncPoolCreation');
  const isAsyncEnabled = asyncCreationVersion === 'enabled';

  const navigate = useNavigate();
  const navigateToDocumentLibrary = () => {
    navigate('/portfolio/document-library');
  };

  const { data, error: queryError } = useQuery<GetPools>(GET_POOLS, {
    fetchPolicy: 'cache-and-network',
    pollInterval: 5000,
  });
  const [createPublicOfferingMutation] = useMutation<
    CreatePublicOffering,
    CreatePublicOfferingVariables
  >(CREATE_PUBLIC_OFFERING);
  const [archivePoolMutation] = useMutation<ArchivePools, ArchivePoolsVariables>(ARCHIVE_POOL);
  const { setPool } = navigation.usePoolUrl();
  const [selectedPools, setSelectedPools] = useState<RowSelectionState>({});

  const onSelectPool = useCallback(
    (updaterOrValue: RowSelectionState | ((old: RowSelectionState) => RowSelectionState)) => {
      const newSelection =
        typeof updaterOrValue === 'function' ? updaterOrValue(selectedPools) : updaterOrValue;
      setSelectedPools(newSelection);
      const pools = data?.user.company.pools;
      const selectedKey = keys(newSelection)[0];
      if (pools && selectedKey !== undefined) {
        const index = Number(selectedKey);
        if (!isNaN(index) && pools[index]) {
          setPool(pools[index].id);
        }
      }
    },
    [data?.user.company.pools, selectedPools, setPool],
  );
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [offeringResultSummary, setOfferingResultSummary] = useState<{
    originalLoanCount: number;
    actualLoanCount: number;
  } | null>(null);

  const offeringCreatedDialog = useDialog();
  const offeringCreationStartedDialog = useDialog();
  const errorDialog = useDialog();
  const archiveConfirmDialog = useDialog();

  useEffect(() => {
    if (selectedPools) {
      const pools = data?.user.company.pools;

      const selectedPoolsArray = pools?.filter((_, index) => selectedPools[index]) || [];

      setSelectedPoolsData(transformSelectedPoolsData(selectedPoolsArray));
    }
  }, [data?.user.company.pools, selectedPools, setSelectedPoolsData]);

  useUnmountEffect(() => {
    setSelectedPoolsData([]);
  });

  const createOffering = async (formValues: FormValues) => {
    const pools = data?.user.company.pools;

    const selectedPoolsArray = pools?.filter((_, index) => selectedPools[index]) || [];

    try {
      const { ownershipType: _, ...mutationValues } = formValues;
      let hasAsyncJob = false;
      let totalOriginalLoanCount = 0;
      let totalActualLoanCount = 0;
      let haveAllActualCounts = true;

      for (const pool of selectedPoolsArray) {
        totalOriginalLoanCount += pool.loan_count ?? 0;
        const result = await createPublicOfferingMutation({
          variables: {
            ...mutationValues,
            listingId: pool.id,
            minimumCarve: formValues.minimumCarve ? Math.round(formValues.minimumCarve * 100) : 0,
            pricingRate:
              mutationValues.pricingRate != null
                ? Math.round(mutationValues.pricingRate * 100)
                : undefined,
            servicingFeeRate:
              mutationValues.servicingFeeRate != null
                ? Math.round(mutationValues.servicingFeeRate * 100)
                : undefined,
          },
          refetchQueries: isAsyncEnabled ? [GET_COMPANY_JOBS] : [],
        });

        const offeringResult = result.data?.createPublicOffering;
        if (isAsyncEnabled && offeringResult?.__typename === 'OfferingCreationJob') {
          hasAsyncJob = true;
        } else if (offeringResult?.__typename === 'PublicListing') {
          if (offeringResult.loan_count == null) {
            haveAllActualCounts = false;
          } else {
            totalActualLoanCount += offeringResult.loan_count;
          }
        }
      }

      if (hasAsyncJob) {
        offeringCreationStartedDialog.openDialog();
      } else {
        setOfferingResultSummary(
          haveAllActualCounts
            ? {
                originalLoanCount: totalOriginalLoanCount,
                actualLoanCount: totalActualLoanCount,
              }
            : null,
        );
        offeringCreatedDialog.openDialog();
      }
    } catch (error) {
      console.error('Error in creating offering:', error);

      // Extract error message from GraphQL error
      let userErrorMessage = 'An unexpected error occurred while creating the offering.';

      if (error && typeof error === 'object') {
        // Apollo GraphQL error structure
        if ('graphQLErrors' in error) {
          const graphQLErrors = (error as any).graphQLErrors;
          if (graphQLErrors && graphQLErrors.length > 0) {
            userErrorMessage = graphQLErrors[0].message;
          }
        }
        // Network error
        else if ('networkError' in error) {
          const networkError = (error as any).networkError;
          if (networkError && networkError.message) {
            userErrorMessage = `Network error: ${networkError.message}`;
          } else {
            userErrorMessage = 'Network error. Please check your connection and try again.';
          }
        }
        // Direct error message
        else if ('message' in error) {
          userErrorMessage = (error as Error).message;
        }
      } else if (typeof error === 'string') {
        userErrorMessage = error;
      }

      setErrorMessage(userErrorMessage);
      errorDialog.openDialog();
    }
  };

  const selectedPoolKey = Object.keys(selectedPools)[0];
  const selectedPoolIndex = parseInt(selectedPoolKey, 10);
  const selectedPool = data?.user.company.pools[selectedPoolIndex];
  const showParticipation = isCreditUnion || selectedPool?.asset_class === AssetClass.CRE;
  const canCreateOffering =
    selectedPool &&
    'is_public' in selectedPool &&
    !selectedPool.is_public &&
    Object.keys(selectedPools).length > 0 &&
    documents &&
    documents.length > 0;

  const createOfferingTooltipText = canCreateOffering
    ? ''
    : documents === undefined || documents.length === 0
      ? 'You must have at least one template document to create an offering.'
      : 'You must select a pool to create an offering.';

  const canArchivePool = isArchiveEnabled && selectedPool && Object.keys(selectedPools).length > 0;

  const archivePoolTooltipText = canArchivePool
    ? ''
    : !isArchiveEnabled
      ? 'Archive functionality is not available in the current mode.'
      : 'You must select a pool to archive it.';

  const navigateToMarketHome = () => {
    navigate('/marketplace/market-home');
  };

  const archivePool = async () => {
    if (!selectedPool) return;

    try {
      const result = await archivePoolMutation({
        variables: {
          input: {
            pool_ids: [selectedPool.id],
          },
        },
      });

      const firstResult = result.data?.archivePools?.results?.[0];
      const topLevelSuccess = result.data?.archivePools?.success === true;
      const perResultSuccess = firstResult ? firstResult.success === true : false;

      if (topLevelSuccess && perResultSuccess) {
        toast.success('Pool archived successfully');
        setSelectedPools({});
      } else {
        const message =
          result.data?.archivePools?.message ||
          firstResult?.message ||
          'Failed to archive pool. Please try again.';
        toast.error(message);
      }
    } catch (error) {
      console.error('Error in archiving pool:', error);

      let userErrorMessage = 'An unexpected error occurred while archiving the pool.';

      toast.error(userErrorMessage);
    }
  };

  const handleArchiveClick = () => {
    archiveConfirmDialog.openDialog();
  };

  return (
    <PortfolioLayout
      actions={
        <div className="flex">
          <ModalNotStyled
            trigger={
              <Button
                data-tooltip-id={`create-offering-tooltip`}
                disabled={!canCreateOffering}
                size="large"
                type="primary_light"
                className="w-[160px]"
                label="Create Offering"
              >
                Create Offering
              </Button>
            }
          >
            {({ closeModal }) => (
              <CreateOfferingForm
                closeModal={closeModal}
                createOffering={createOffering}
                showParticipation={showParticipation}
                poolLoanCount={selectedPool?.loan_count}
              />
            )}
          </ModalNotStyled>
          {isArchiveEnabled && (
            <Button
              data-tooltip-id={`archive-pool-tooltip`}
              disabled={!canArchivePool}
              size="large"
              type="primary"
              className="ml-[20px] w-[160px]"
              label="Archive Pool"
              onClick={handleArchiveClick}
            >
              Archive
            </Button>
          )}
        </div>
      }
    >
      <>
        {!canCreateOffering && (
          <Tooltip id={`create-offering-tooltip`} place="left" variant="info">
            <span>{createOfferingTooltipText}</span>
          </Tooltip>
        )}
        {isArchiveEnabled && !canArchivePool && (
          <Tooltip id={`archive-pool-tooltip`} place="left" variant="info">
            <span>{archivePoolTooltipText}</span>
          </Tooltip>
        )}
        {queryError ? (
          <div>Error retrieving pools</div>
        ) : (
          <>
            {!loading && documents !== undefined && documents.length === 0 && (
              <div className="bg-blue-600 text-center">
                <div className="flex items-center justify-center gap-[30px] p-2.5 text-white">
                  You must have at least one template document to create an offering.
                  <Button
                    size="small"
                    className="!min-h-[24px]"
                    type={'text_button'}
                    onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                      e.preventDefault();
                      navigateToDocumentLibrary();
                    }}
                  >
                    Go to Document Library
                  </Button>
                </div>
              </div>
            )}
            <PoolTable
              data={data?.user.company.pools ?? []}
              selection={{
                selected: selectedPools,
                onSelectionChange: onSelectPool,
                enableMultiRowSelection: false,
              }}
            />
          </>
        )}
      </>

      <Dialog dialog={offeringCreatedDialog}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <img src={offeringCreatedImgPath} alt="offering created" />{' '}
          <div className="mt-[50px] text-center text-[24px] font-medium leading-[36px] text-white">
            Offering Created
          </div>
          <div
            className=" ont-[Heebo] mb-[30px] mt-5 w-[360px] text-center text-base font-medium leading-6 text-white"
            data-testid="offering-created-summary"
          >
            {offeringResultSummary &&
            offeringResultSummary.originalLoanCount > offeringResultSummary.actualLoanCount ? (
              <>
                Offering created with{' '}
                <strong>
                  {offeringResultSummary.actualLoanCount} of{' '}
                  {offeringResultSummary.originalLoanCount} loans
                </strong>
                . {offeringResultSummary.originalLoanCount - offeringResultSummary.actualLoanCount}{' '}
                {offeringResultSummary.originalLoanCount - offeringResultSummary.actualLoanCount ===
                1
                  ? 'loan was'
                  : 'loans were'}{' '}
                excluded because they are partially owned.
              </>
            ) : offeringResultSummary && offeringResultSummary.actualLoanCount > 0 ? (
              <>
                Offering created with{' '}
                <strong>
                  {offeringResultSummary.actualLoanCount}{' '}
                  {offeringResultSummary.actualLoanCount === 1 ? 'loan' : 'loans'}
                </strong>
                .
              </>
            ) : (
              <>Offering created successfully.</>
            )}
          </div>
          <DialogActions className="w-[150px]">
            <Button
              size="medium"
              onClick={() => {
                offeringCreatedDialog.closeDialog();
                setOfferingResultSummary(null);
                navigateToMarketHome();
              }}
            >
              OK
            </Button>
          </DialogActions>
        </div>
      </Dialog>

      <Dialog dialog={offeringCreationStartedDialog}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <img src={offeringCreatedImgPath} alt="offering creation started" />{' '}
          <div className="mt-[50px] text-center text-[24px] font-medium leading-[36px] text-white">
            Offering Creation Started
          </div>
          <div className="mb-[30px] mt-5 w-[300px] text-base font-medium leading-6 text-white">
            Your offering is being created. This may take a few minutes for large pools. You can
            track progress in the Jobs dropdown in the header.
          </div>
          <DialogActions className="w-[150px]">
            <Button size="medium" onClick={() => offeringCreationStartedDialog.closeDialog()}>
              OK
            </Button>
          </DialogActions>
        </div>
      </Dialog>

      <Dialog dialog={errorDialog}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div
            style={{
              width: '64px',
              height: '64px',
              borderRadius: '50%',
              backgroundColor: '#FEE2E2',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '24px',
            }}
          >
            <svg
              width="32"
              height="32"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#DC2626"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="12" cy="12" r="10" />
              <line x1="15" y1="9" x2="9" y2="15" />
              <line x1="9" y1="9" x2="15" y2="15" />
            </svg>
          </div>
          <StyledHeading className="mb-4 text-red-600">Unable to Create Offering</StyledHeading>
          <DialogBodyText className="mb-6 w-[400px] text-center">{errorMessage}</DialogBodyText>
          <DialogActions className="w-[150px]">
            <Button
              size="medium"
              onClick={() => {
                errorDialog.closeDialog();
                setErrorMessage('');
              }}
            >
              OK
            </Button>
          </DialogActions>
        </div>
      </Dialog>

      <ConfirmDialog
        dialog={archiveConfirmDialog}
        message="Are you sure you want to archive this pool?"
        confirmButtonText="Archive"
        onConfirm={archivePool}
        image={validationImgPath}
        imageAlt="Validation"
        imageW="300px"
      />
    </PortfolioLayout>
  );
};

export default PoolManager;
